// Host-owned mount points in the right sidebar. The left sidebar is Core-only.
// Product code receives only its card node and a lifecycle context.
const owners = new Set();
let rendering = false;
const noop = () => undefined;
function report(error) { console.warn('[sidebar-cards]', error); }
function synchronous(result) {
  if (result && typeof result.then === 'function') {
    Promise.resolve(result).then(controller => controller?.dispose?.(), noop).catch(report);
    throw new TypeError('Seitenleistenkarten müssen synchron aufgebaut werden.');
  }
  return result;
}
function unmount(card) {
  const mounted = card.mounted;
  card.mounted = null;
  if (!mounted) return;
  mounted.abort.abort();
  try { synchronous(mounted.controller?.dispose?.()); } catch (error) { report(error); }
  mounted.node.remove();
}
function sameContext(a, b) {
  return a?.projectToken === b?.projectToken && a?.sheetId === b?.sheetId;
}
function publicContext(current, abort) {
  return Object.freeze({ sheetId: current.sheetId ?? null, signal: abort.signal });
}
export function createSystemSidebarCards(owner, ports) {
  const scope = { owner, ports, cards: new Map() };
  let disposed = false;
  owners.add(scope);
  function register(definition) {
    if (disposed) throw new Error('Seitenleistenaktivierung wurde beendet.');
    if (!definition || typeof definition.id !== 'string' || !/^[a-zA-Z0-9_-]{1,80}$/.test(definition.id)
      || !Number.isSafeInteger(definition.order ?? 0) || typeof definition.mount !== 'function'
      || scope.cards.size >= 32 || scope.cards.has(definition.id)) throw new TypeError('Ungültige oder doppelte Produktkarte.');
    const card = { id: definition.id, order: definition.order ?? 0, mount: definition.mount, mounted: null };
    scope.cards.set(card.id, card);
    return () => {
      if (scope.cards.get(card.id) !== card) return;
      unmount(card); scope.cards.delete(card.id); renderSystemSidebarCards();
    };
  }
  function dispose() {
    if (disposed) return;
    disposed = true;
    scope.cards.forEach(unmount); scope.cards.clear(); owners.delete(scope);
    renderSystemSidebarCards();
  }
  return Object.freeze({ register, dispose });
}
export function renderSystemSidebarCards() {
  if (rendering) return false;
  rendering = true;
  try {
    const host = typeof document === 'undefined' ? null : document.getElementById('systemSidebarCards');
    let count = 0;
    for (const scope of owners) {
      const current = scope.ports.current();
      const active = host && current && scope.ports.isActive();
      const cards = [...scope.cards.values()].sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
      for (const card of cards) {
        if (!owners.has(scope) || scope.cards.get(card.id) !== card) continue;
        if (!active) { unmount(card); continue; }
        if (card.mounted && (!sameContext(card.mounted.context, current) || card.mounted.node.parentElement !== host)) unmount(card);
        try {
          if (!card.mounted) {
            const node = document.createElement('div'), abort = new AbortController();
            node.dataset.sidebarCardOwner = scope.owner; node.dataset.sidebarCardId = card.id;
            host.appendChild(node);
            card.mounted = { node, abort, context: current, controller: null };
            const controller = synchronous(card.mount(node, publicContext(current, abort)));
            if (controller !== undefined && (!controller || typeof controller !== 'object'
              || (controller.update !== undefined && typeof controller.update !== 'function')
              || (controller.dispose !== undefined && typeof controller.dispose !== 'function'))) throw new TypeError('Ungültiger Kartenlebenszyklus.');
            if (!card.mounted || !owners.has(scope) || scope.cards.get(card.id) !== card) {
              synchronous(controller?.dispose?.()); continue;
            }
            card.mounted.controller = controller;
          } else {
            synchronous(card.mounted.controller?.update?.(publicContext(current, card.mounted.abort)));
          }
          // Reorder existing nodes without recreating inputs or losing their listeners.
          const expected = host.children[count];
          if (expected !== card.mounted.node) host.insertBefore(card.mounted.node, expected || null);
          count += 1;
        } catch (error) { unmount(card); report(error); }
      }
    }
    if (host) host.hidden = count === 0;
    return count > 0;
  } finally { rendering = false; }
}
